import type { Lang } from '../types';

export interface SubscriptionText {
  pageTitle: string;
  subtitle: string;
  suspendedTitle: string;
  suspendedBody: string;
  cardAccountStatus: string;
  cardStatusActive: string;
  cardStatusSuspended: string;
  cardBillableApts: string;
  cardMonthlyPayment: string;
  cardCurrency: string;
  pricingTitle: string;
  pricingMonthly: string;
  pricingAnnual: string;
  pricingNote: string;
  perAptPerMonth: string;
  perAptPerYear: string;
  yourApartmentsTitle: string;
  billedFrom: string;
  perMonth: string;
  paymentHistoryTitle: string;
  noRecords: string;
  tablePeriod: string;
  tableApts: string;
  tableTotal: string;
  tableCycle: string;
  tableStatus: string;
  cycleMonthly: string;
  cycleAnnual: string;
  statusPaid: string;
  statusPending: string;
  statusOverdue: string;
  loading: string;
}

export const subscriptionText: Record<Lang, SubscriptionText> = {
  cs: {
    pageTitle: 'Předplatné',
    subtitle: 'Přehled vašeho tarifu a fakturace',
    suspendedTitle: 'Váš účet je pozastaven.',
    suspendedBody: 'Vytváření a úprava rezervací, přidávání bytů a export dat jsou dočasně zakázány. Pro obnovení přístupu prosím kontaktujte správce nebo proveďte úhradu.',
    cardAccountStatus: 'Stav účtu',
    cardStatusActive: 'Aktivní',
    cardStatusSuspended: 'Pozastaven',
    cardBillableApts: 'Fakturovatelných bytů',
    cardMonthlyPayment: 'Odh. měsíční platba',
    cardCurrency: 'Měna',
    pricingTitle: 'Ceník',
    pricingMonthly: 'Měsíční tarif:',
    pricingAnnual: 'Roční tarif:',
    pricingNote: 'Fakturace začíná od 1. dne měsíce následujícího po přidání bytu. Platbu provádějte bankovním převodem. V případě dotazů kontaktujte správce systému.',
    perAptPerMonth: '/ byt / měsíc',
    perAptPerYear: '/ byt / rok',
    yourApartmentsTitle: 'Vaše byty (fakturovatelné)',
    billedFrom: 'Fakturováno od',
    perMonth: '/měs',
    paymentHistoryTitle: 'Historie plateb',
    noRecords: 'Zatím žádné záznamy',
    tablePeriod: 'Období',
    tableApts: 'Byty',
    tableTotal: 'Celkem',
    tableCycle: 'Tarif',
    tableStatus: 'Stav',
    cycleMonthly: 'Měsíční',
    cycleAnnual: 'Roční',
    statusPaid: 'Zaplaceno',
    statusPending: 'Čeká na platbu',
    statusOverdue: 'Po splatnosti',
    loading: 'Načítám…',
  },
  en: {
    pageTitle: 'Subscription',
    subtitle: 'Overview of your plan and billing',
    suspendedTitle: 'Your account is suspended.',
    suspendedBody: 'Creating and editing reservations, adding apartments and exporting data are temporarily disabled. Please contact the admin or make a payment to restore access.',
    cardAccountStatus: 'Account status',
    cardStatusActive: 'Active',
    cardStatusSuspended: 'Suspended',
    cardBillableApts: 'Billable apartments',
    cardMonthlyPayment: 'Est. monthly payment',
    cardCurrency: 'Currency',
    pricingTitle: 'Pricing',
    pricingMonthly: 'Monthly plan:',
    pricingAnnual: 'Annual plan:',
    pricingNote: 'Billing starts from the 1st of the month following apartment addition. Payment via bank transfer. Contact the system admin for questions.',
    perAptPerMonth: '/ apt / month',
    perAptPerYear: '/ apt / year',
    yourApartmentsTitle: 'Your apartments (billable)',
    billedFrom: 'Billed from',
    perMonth: '/mo',
    paymentHistoryTitle: 'Payment history',
    noRecords: 'No records yet',
    tablePeriod: 'Period',
    tableApts: 'Apts',
    tableTotal: 'Total',
    tableCycle: 'Plan',
    tableStatus: 'Status',
    cycleMonthly: 'Monthly',
    cycleAnnual: 'Annual',
    statusPaid: 'Paid',
    statusPending: 'Pending',
    statusOverdue: 'Overdue',
    loading: 'Loading…',
  },
  vi: {
    pageTitle: 'Gói đăng ký',
    subtitle: 'Tổng quan gói cước và thanh toán',
    suspendedTitle: 'Tài khoản của bạn bị tạm ngưng.',
    suspendedBody: 'Tạo và chỉnh sửa lịch đặt, thêm căn hộ và xuất dữ liệu tạm thời bị vô hiệu hóa. Vui lòng liên hệ quản trị viên hoặc thanh toán để khôi phục quyền truy cập.',
    cardAccountStatus: 'Trạng thái tài khoản',
    cardStatusActive: 'Hoạt động',
    cardStatusSuspended: 'Tạm ngưng',
    cardBillableApts: 'Căn hộ tính phí',
    cardMonthlyPayment: 'Ước tính tháng',
    cardCurrency: 'Tiền tệ',
    pricingTitle: 'Bảng giá',
    pricingMonthly: 'Gói tháng:',
    pricingAnnual: 'Gói năm:',
    pricingNote: 'Thanh toán bắt đầu từ ngày 1 tháng sau khi thêm căn hộ. Thanh toán qua chuyển khoản ngân hàng. Liên hệ quản trị viên nếu có câu hỏi.',
    perAptPerMonth: '/ căn / tháng',
    perAptPerYear: '/ căn / năm',
    yourApartmentsTitle: 'Căn hộ của bạn (tính phí)',
    billedFrom: 'Tính phí từ',
    perMonth: '/tháng',
    paymentHistoryTitle: 'Lịch sử thanh toán',
    noRecords: 'Chưa có bản ghi',
    tablePeriod: 'Giai đoạn',
    tableApts: 'Căn hộ',
    tableTotal: 'Tổng',
    tableCycle: 'Gói',
    tableStatus: 'Trạng thái',
    cycleMonthly: 'Hàng tháng',
    cycleAnnual: 'Hàng năm',
    statusPaid: 'Đã thanh toán',
    statusPending: 'Chờ thanh toán',
    statusOverdue: 'Quá hạn',
    loading: 'Đang tải…',
  },
  uk: {
    pageTitle: 'Підписка',
    subtitle: 'Огляд вашого тарифу та виставлення рахунків',
    suspendedTitle: 'Ваш обліковий запис призупинено.',
    suspendedBody: 'Створення та редагування бронювань, додавання апартаментів та експорт даних тимчасово заблоковані. Зверніться до адміністратора або здійсніть оплату для відновлення доступу.',
    cardAccountStatus: 'Статус облікового запису',
    cardStatusActive: 'Активний',
    cardStatusSuspended: 'Призупинено',
    cardBillableApts: 'Апартаментів для оплати',
    cardMonthlyPayment: 'Прибл. місячний платіж',
    cardCurrency: 'Валюта',
    pricingTitle: 'Тарифи',
    pricingMonthly: 'Місячний тариф:',
    pricingAnnual: 'Річний тариф:',
    pricingNote: 'Нарахування починається з 1-го числа місяця, наступного після додавання апартаменту. Оплата банківським переказом. З питань звертайтесь до адміністратора.',
    perAptPerMonth: '/ апарт. / місяць',
    perAptPerYear: '/ апарт. / рік',
    yourApartmentsTitle: 'Ваші апартаменти (до оплати)',
    billedFrom: 'Нараховується від',
    perMonth: '/міс',
    paymentHistoryTitle: 'Історія платежів',
    noRecords: 'Записів ще немає',
    tablePeriod: 'Період',
    tableApts: 'Апартаменти',
    tableTotal: 'Разом',
    tableCycle: 'Тариф',
    tableStatus: 'Статус',
    cycleMonthly: 'Місячний',
    cycleAnnual: 'Річний',
    statusPaid: 'Оплачено',
    statusPending: 'Очікує оплати',
    statusOverdue: 'Прострочено',
    loading: 'Завантаження…',
  },
};
